import { useState } from 'react';
import { Check, ChevronsUpDown, FolderTree, LayoutGrid, LayoutList, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { type Device, type Group } from '@/lib/traccar-api';
import { useSidebar } from '@/components/ui/sidebar';

interface HeaderControlsProps {
  lastUpdate: Date | null;
  devices?: Device[];
  groups?: Group[];
  selectedDeviceId?: number | null;
  selectedGroupId?: number | null;
  onDeviceFilterChange?: (deviceId: number | null) => void;
  onGroupFilterChange?: (groupId: number | null) => void;
  showChartsRow?: boolean;
  onToggleChartsRow?: () => void;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

export const HeaderControls = ({
  devices = [],
  groups = [],
  selectedDeviceId,
  selectedGroupId,
  onDeviceFilterChange,
  onGroupFilterChange,
  showChartsRow,
  onToggleChartsRow,
  searchQuery = '',
  onSearchChange,
}: HeaderControlsProps) => {
  const { t } = useTranslation();
  const { state: sidebarState } = useSidebar();
  const [open, setOpen] = useState(false);
  const sortedDevices = [...devices].sort((a, b) => a.name.localeCompare(b.name));
  const sortedGroups = [...groups].sort((a, b) => a.name.localeCompare(b.name));
  const selectedDevice = devices.find(d => d.id === selectedDeviceId);

  return (
    <>
      {onSearchChange && (
        <div className="relative hidden @lg:block">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
          <Input
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={t('devices.searchPlaceholder')}
            className="pl-8 h-8 w-[180px] text-sm"
          />
        </div>
      )}
      {onToggleChartsRow !== undefined && sidebarState !== 'expanded' && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              onClick={onToggleChartsRow}
              className="gap-2 hidden @xl:flex"
            >
              {showChartsRow ? <LayoutList className="h-4 w-4" /> : <LayoutGrid className="h-4 w-4" />}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {showChartsRow ? t('dashboard.hideCharts') : t('dashboard.showCharts')}
          </TooltipContent>
        </Tooltip>
      )}
      {onGroupFilterChange && groups.length > 0 && (
        <Select
          value={selectedGroupId?.toString() ?? 'all'}
          onValueChange={(value) => onGroupFilterChange(value === 'all' ? null : Number(value))}
        >
          <SelectTrigger className="hidden @xl:flex w-[160px] text-sm gap-2">
            <FolderTree className="h-3 w-3 sm:h-4 sm:w-4" />
            <SelectValue placeholder={t('devices.allGroups')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('devices.allGroups')}</SelectItem>
            {sortedGroups.map((group) => (
              <SelectItem key={group.id} value={group.id.toString()}>
                {group.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      {onDeviceFilterChange && devices.length > 0 && (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              role="combobox"
              aria-expanded={open}
              className="w-[140px] @sm:w-[200px] justify-between text-xs @sm:text-sm font-normal"
            >
              <span className="truncate">
                {selectedDevice ? selectedDevice.name : t('devices.allDevices')}
              </span>
              <ChevronsUpDown className="h-3 w-3 @sm:h-4 @sm:w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[220px] p-0" align="end">
            <Command>
              <CommandInput placeholder={t('devices.searchPlaceholder')} className="h-9" />
              <CommandList>
                <CommandEmpty>{t('devices.noDevicesMatch')}</CommandEmpty>
                <CommandGroup>
                  <CommandItem
                    value="all"
                    onSelect={() => { onDeviceFilterChange(null); setOpen(false); }}
                  >
                    <Check className={cn('mr-2 h-4 w-4', selectedDeviceId == null ? 'opacity-100' : 'opacity-0')} />
                    {t('devices.allDevices')}
                  </CommandItem>
                  {sortedDevices.map((device) => (
                    <CommandItem
                      key={device.id}
                      value={device.name}
                      onSelect={() => { onDeviceFilterChange(device.id); setOpen(false); }}
                    >
                      <Check className={cn('mr-2 h-4 w-4', selectedDeviceId === device.id ? 'opacity-100' : 'opacity-0')} />
                      {device.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      )}
    </>
  );
};
